import {Box} from '@mui/material';
import {AggregationSurveyIntroduction} from 'crqlar-react-common/dist/models/aggregation/surveyAggregation.model';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {StatGrid} from '../../insights/kit/StatGrid';
import {InsightsLoading} from '../../insights/kit/insightsLoading';
import {SurveyFactsCard} from './SurveyFactsCard';
import {toStatCards} from './data/surveyTargets.util';

const FACTS_BASIS = 380;
const STATS_BASIS = 620;

interface Props {
    introduction?: AggregationSurveyIntroduction;
    /** The survey's colour, shown beside its name. */
    color?: string;
    loading?: InsightsLoading;
    matches?: (title: string, key: string) => boolean;
}

export function SurveyInsightsBand({introduction, color, loading, matches}: Props) {
    const {t} = useTranslation();
    const stats = useMemo(() => toStatCards(introduction, t), [introduction, t]);

    const skeleton = loading === 'skeleton';

    const surveyName = introduction?.survey.name ?? '';
    const showFacts = matches == null || matches(surveyName, surveyName);

    const visibleStats = useMemo(
        () => (matches == null ? stats : stats.filter(stat => matches(stat.title, stat.id))),
        [matches, stats],
    );

    if (!showFacts && visibleStats.length === 0) {
        return null;
    }

    return (
        <Box
            component={'section'}
            aria-label={skeleton ? undefined : introduction?.survey.name ?? undefined}
            sx={{display: 'flex', flexWrap: 'wrap', alignItems: 'stretch', gap: 2, minWidth: 0}}
        >
            {showFacts && (
                <Box sx={{flex: `1 1 ${FACTS_BASIS}px`, minWidth: 0}}>
                    <SurveyFactsCard survey={introduction?.survey} color={color} loading={skeleton}/>
                </Box>
            )}

            {visibleStats.length > 0 && (
                <Box sx={{flex: `2 1 ${STATS_BASIS}px`, minWidth: 0}}>
                    <StatGrid items={visibleStats} loading={loading}/>
                </Box>
            )}
        </Box>
    );
}
